import { trpc } from "@/lib/trpc";
import { UNAUTHED_ERR_MSG } from '@shared/const';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { TRPCClientError, httpBatchLink } from "@trpc/client";
import { createRoot } from "react-dom/client";
import superjson from "superjson";
import App from "./App";
import { getLoginUrl } from "./const";
import { initializeMonitoring } from "./lib/firebase-monitoring";
import "./lib/i18n";
import "./index.css";

// Google Tag Manager is injected into static HTML by the `gtm` Vite plugin
// in vite.config.ts (only when VITE_GTM_ID is set). Doing it from JS at
// runtime would skip Google's HTML scanner and the GTM verifier reports
// "Tag not detected".

// Initialize Firebase Analytics and Crashlytics (only once)
if (typeof window !== 'undefined' && !window.__firebaseMonitoringInitialized) {
  window.__firebaseMonitoringInitialized = true;
  initializeMonitoring().catch(error => console.warn('Monitoring initialization warning:', error));
}

// Global error handlers for debugging (only once)
if (typeof window !== 'undefined' && !window.__errorHandlersInitialized) {
  window.__errorHandlersInitialized = true;
  window.addEventListener('error', (event) => {
    console.error('[Global Error]', event.error);
  });
  window.addEventListener('unhandledrejection', (event) => {
    console.error('[Unhandled Promise Rejection]', event.reason);
  });
  // Track page visibility for analytics
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      console.log('[Analytics] Page hidden');
    } else {
      console.log('[Analytics] Page visible');
    }
  });
}

// Declare global window properties
declare global {
  interface Window {
    __firebaseMonitoringInitialized?: boolean;
    __errorHandlersInitialized?: boolean;
  }
}

const queryClient = new QueryClient();

const redirectToLoginIfUnauthorized = (error: unknown) => {
  if (!(error instanceof TRPCClientError)) return;
  if (typeof window === "undefined") return;

  const isUnauthorized = error.message === UNAUTHED_ERR_MSG;

  if (!isUnauthorized) return;

  window.location.href = getLoginUrl();
};

queryClient.getQueryCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.query.state.error;
    redirectToLoginIfUnauthorized(error);
    console.error("[API Query Error]", error);
  }
});

queryClient.getMutationCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.mutation.state.error;
    redirectToLoginIfUnauthorized(error);
    console.error("[API Mutation Error]", error);
  }
});

const trpcClient = trpc.createClient({
  links: [
    httpBatchLink({
      url: '/api/trpc',
      transformer: superjson,
      fetch(input, init) {
        return globalThis.fetch(input, {
          ...(init ?? {}),
          credentials: "include",
        });
      },
    }),
  ],
});

const root = document.getElementById("root");
if (!root) {
  console.error('[Fatal Error] Root element not found');
  document.body.innerHTML = '<div style="color: red; padding: 20px;">Fatal Error: Root element not found</div>';
} else {
  try {
    createRoot(root).render(
      <trpc.Provider client={trpcClient} queryClient={queryClient}>
        <QueryClientProvider client={queryClient}>
          <App />
        </QueryClientProvider>
      </trpc.Provider>
    );
  } catch (error) {
    console.error('[React Render Error]', error);
    root.innerHTML = '<div style="color: red; padding: 20px;">Error rendering application. Check console for details.</div>';
  }
}
